// FR-AD7 / FR-API6a operator tokens: `grackle_opt_…` credentials for the staff MCP at
// /ops/mcp. GET/POST /ops/tokens, DELETE /ops/tokens/{id}. Minting needs a platform admin
// with a live step-up — OpsLayout already gates the whole console on that, so the only way
// to see a step-up failure here is the 15-minute window lapsing mid-session. Expiry is one of a
// fixed menu, including never (SD62); the secret is shown once and never again.
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Badge, Card, Select } from "@grackle/ui";
import { api, ApiFail } from "../api/client";
import type { AccessTokenCreated, AccessTokenOut, ListResponse } from "../api/types";
import { Button, Field, Notice } from "../components/ui";
import { TokenAccessPicker, accessToScopes, scopesLabel, type TokenAccess } from "../components/TokenScopes";
import "../admin/admin.css";

/** SD62: the lifetimes the server accepts (`ops.rs::OPERATOR_TOKEN_DAYS`); "never" is null. */
export const OPERATOR_TOKEN_EXPIRY_OPTIONS = [
  { value: "7", label: "7 days" },
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
  { value: "180", label: "6 months" },
  { value: "365", label: "1 year" },
  { value: "never", label: "Never" },
];

function clock(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : "—";
}

export default function OpsTokensPage() {
  const [tokens, setTokens] = useState<AccessTokenOut[] | null>(null);
  const [name, setName] = useState("");
  const [expiry, setExpiry] = useState("30");
  const [access, setAccess] = useState<TokenAccess>("read");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<AccessTokenCreated | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const load = useCallback(() => {
    api<ListResponse<AccessTokenOut>>("GET", "/ops/tokens")
      .then((res) => setTokens(res.items))
      .catch((err) => setError(err instanceof ApiFail ? err.message : "Couldn't load operator tokens."));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const canCreate = name.trim().length > 0;

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    if (!canCreate) return;
    setCreating(true);
    setError(null);
    try {
      const res = await api<AccessTokenCreated>("POST", "/ops/tokens", {
        name: name.trim(),
        expires_in_days: expiry === "never" ? null : Number(expiry),
        scopes: accessToScopes(access, "ops:"),
      });
      setCreated(res);
      setName("");
      load();
    } catch (err) {
      setError(
        err instanceof ApiFail && err.status === 401
          ? "Your step-up has expired. Reload the console and re-enter your password."
          : err instanceof ApiFail
            ? err.message
            : "Couldn't create that token.",
      );
    } finally {
      setCreating(false);
    }
  }

  async function onRevoke(id: string) {
    setRevokingId(id);
    setError(null);
    try {
      await api("DELETE", `/ops/tokens/${id}`);
      if (created?.id === id) setCreated(null);
      load();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't revoke that token.");
    } finally {
      setRevokingId(null);
    }
  }

  async function onCopy(secret: string) {
    try {
      await navigator.clipboard.writeText(secret);
    } catch {
      // Clipboard may be denied; the secret is still on screen to copy by hand.
    }
  }

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Operator tokens</h1>
          <p>
            Staff credentials for the operator MCP at <span className="mono">/ops/mcp</span>. Shown once,
            and stop working when they expire, when you revoke them, or the moment you are removed as a
            platform admin. Every call is written to the ops audit trail.
          </p>
        </div>
      </header>

      {error ? <Notice tone="error">{error}</Notice> : null}

      <form onSubmit={onCreate} className="admin-inline-form" style={{ alignItems: "flex-end" }}>
        <Field label="Token name" value={name} onChange={(e) => setName(e.target.value)} placeholder="incident response" />
        <Select label="Expires" value={expiry} onChange={setExpiry} options={OPERATOR_TOKEN_EXPIRY_OPTIONS} />
        <TokenAccessPicker
          label="Scopes"
          value={access}
          onChange={setAccess}
        />
        <Button type="submit" loading={creating} disabled={!canCreate}>
          Mint token
        </Button>
      </form>

      {created ? (
        <div className="list-row" role="status">
          <div className="list-row__body">
            <div className="list-row__sub">Copy this now — it will not be shown again.</div>
            <div className="list-row__sub mono" style={{ wordBreak: "break-all" }}>
              {created.secret}
            </div>
          </div>
          <Button variant="secondary" size="sm" onClick={() => onCopy(created.secret)}>
            Copy
          </Button>
        </div>
      ) : null}

      {tokens === null ? (
        <p className="admin-row-sub">Loading…</p>
      ) : tokens.length === 0 ? (
        <p className="admin-row-sub">No live operator tokens.</p>
      ) : (
        <Card size="flush" className="admin-table-card">
<table className="admin-table">
          <thead>
            <tr>
              <th className="gk-overline">Name</th>
              <th className="gk-overline">Scopes</th>
              <th className="gk-overline">Last used</th>
              <th className="gk-overline">Expires</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {tokens.map((t) => (
              <tr key={t.id}>
                <td>
                  <div className="admin-row-name">{t.name}</div>
                  <div className="admin-row-sub mono">{t.token_prefix}…</div>
                </td>
                <td>
                  <Badge size="sm">{scopesLabel(t.scopes)}</Badge>
                  <div className="admin-row-sub mono">{t.scopes.join(", ")}</div>
                </td>
                <td className="mono">{t.last_used_at ? clock(t.last_used_at) : "never"}</td>
                <td className="mono">{t.expires_at ? clock(t.expires_at) : "Never"}</td>
                <td className="row-actions">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onRevoke(t.id)}
                    disabled={revokingId === t.id}
                  >
                    {revokingId === t.id ? "Revoking…" : "Revoke"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
</Card>
      )}
    </>
  );
}
